import React from 'react';
import { render, screen, waitFor } from '@testing-library/react';

import useFilterOptions from './useFilterOptions';

const OPTIONS_BODY = {
  permissions: ['Member'],
  jobRoles: [{ label: 'Technician', value: 'technician' }],
};

// See useUsers.test.js — a probe rather than renderHook, and waitFor rather than
// async act(), which React 16.8 predates.
let hook;

function Probe({ refreshKey = 0 }) {
  hook = useFilterOptions(refreshKey);

  return (
    <div>
      <span data-testid="permissions">
        {hook.permissionOptions.map((o) => o.label).join(',')}
      </span>
      <span data-testid="jobRoles">
        {hook.jobRoleOptions.map((o) => o.label).join(',')}
      </span>
      <span data-testid="accounts">
        {hook.accountOptions.map((o) => o.label).join(',')}
      </span>
    </div>
  );
}

const at = (id) => screen.getByTestId(id).textContent;

const respondWith = ({ accounts = [{ label: 'Alpha', value: 1 }] } = {}) => {
  global.fetch = jest.fn().mockImplementation((url) => {
    const body = url.startsWith('/management/users/accounts')
      ? { accounts }
      : OPTIONS_BODY;

    return Promise.resolve({ ok: true, json: async () => body });
  });
};

const accountCalls = () =>
  global.fetch.mock.calls
    .map(([url]) => url)
    .filter((url) => url.startsWith('/management/users/accounts'));

const boundedCalls = () =>
  global.fetch.mock.calls
    .map(([url]) => url)
    .filter((url) => url.startsWith('/management/users/filter_options'));

afterEach(() => {
  delete global.fetch;
  hook = undefined;
});

describe('management/Users useFilterOptions', () => {
  it('starts with nothing to offer', () => {
    respondWith();

    render(<Probe />);

    expect(at('permissions')).toEqual('');
    expect(at('accounts')).toEqual('');
  });

  // Permissions and job roles are bounded, so they load once on mount.
  it('loads the bounded lists on mount', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() => expect(at('permissions')).toEqual('Member'));
    expect(at('jobRoles')).toEqual('Technician');
  });

  it('pairs each role name as its own label and value', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() =>
      expect(hook.permissionOptions).toEqual([
        { label: 'Member', value: 'Member' },
      ]),
    );
  });

  // The accounts menu opens before anything is typed, so it needs a first page.
  it('loads an initial page of accounts on mount', async () => {
    respondWith();

    render(<Probe />);

    await waitFor(() => expect(at('accounts')).toEqual('Alpha'));
  });

  // The reported gap: UserFilterOptions.accounts offers only accounts that
  // already have a listable user, so creating a user on an account that had none
  // makes it an option for the first time. Until this refetched, the account the
  // new user belongs to was missing from the very menu you would filter by to
  // find them.
  describe('refreshing after a user is created', () => {
    it('re-asks for the accounts', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      respondWith({
        accounts: [{ label: 'Alpha', value: 1 }, { label: 'Gamma', value: 3 }],
      });
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(at('accounts')).toEqual('Alpha,Gamma'));
    });

    // Roles are global and the job role list is static, so a create can only pick
    // from what is already offered. Refetching them would be a request per create
    // for an answer that cannot have changed.
    it('leaves the bounded lists alone', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('permissions')).toEqual('Member'));

      global.fetch.mockClear();
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(boundedCalls()).toEqual([]);
    });

    // Otherwise the refresh silently widens a menu the user had narrowed.
    it('re-asks the same question when a search is in force', async () => {
      respondWith();
      const { rerender } = render(<Probe refreshKey={0} />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      hook.searchAccounts('bet');
      await waitFor(() => expect(accountCalls().length).toEqual(2));

      global.fetch.mockClear();
      rerender(<Probe refreshKey={1} />);

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });
  });

  describe('searching accounts', () => {
    it('replaces the options with the matches', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      respondWith({ accounts: [{ label: 'Beta', value: 2 }] });
      hook.searchAccounts('bet');

      await waitFor(() => expect(at('accounts')).toEqual('Beta'));
    });

    it('sends the term to the server', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.searchAccounts('bet');

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });

    // SearchInput's searchOn="change" fires per keystroke and its documented
    // debounceTimeout is never read by the component, so this is the only thing
    // stopping a request per character.
    it('collapses a burst of keystrokes into one request', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch.mockClear();
      hook.searchAccounts('b');
      hook.searchAccounts('be');
      hook.searchAccounts('bet');

      await waitFor(() => expect(accountCalls().length).toEqual(1));
      expect(accountCalls()[0]).toContain('q=bet');
    });

    it('empties the options rather than throwing when the search fails', async () => {
      respondWith();
      render(<Probe />);
      await waitFor(() => expect(at('accounts')).toEqual('Alpha'));

      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });
      hook.searchAccounts('bet');

      await waitFor(() => expect(at('accounts')).toEqual(''));
    });
  });

  // A failed option list should not take the table down with it — the rows are
  // the part of the page that matters.
  describe('when the option endpoints fail', () => {
    it('leaves the bounded lists empty without throwing', async () => {
      global.fetch = jest.fn().mockResolvedValue({ ok: false, status: 500 });

      render(<Probe />);

      await waitFor(() => expect(global.fetch).toHaveBeenCalled());
      expect(at('permissions')).toEqual('');
      expect(at('jobRoles')).toEqual('');
      expect(at('accounts')).toEqual('');
    });
  });
});
